import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How do you test casinos?',
      answer: 'We test every casino through real gameplay. Our team creates accounts, makes deposits, plays games, processes withdrawals, and contacts customer support. We verify licenses, check security measures, test mobile compatibility, and evaluate bonus terms. Each casino undergoes at least 30 days of testing before we publish a review.'
    },
    {
      question: 'Are your reviews biased?',
      answer: 'No. While we earn affiliate commissions when players sign up through our links, we never let this influence our reviews. We\'ve rejected partnerships with casinos that don\'t meet our standards, even when they offered higher commissions. Our reputation depends on honest reviews, and we protect that above all else.'
    },
    {
      question: 'How often do you update reviews?',
      answer: 'We update reviews whenever we discover significant changes—new bonuses, policy updates, security issues, or changes in withdrawal times. Major reviews are refreshed at least quarterly, and we monitor all reviewed casinos for news and player feedback.'
    },
    {
      question: 'What makes a casino "recommended"?',
      answer: 'A recommended casino must have: a valid UK Gambling Commission license, fast withdrawal processing (under 48 hours), responsive customer support, fair bonus terms, a wide game selection, strong security measures, and positive player feedback. We also verify that the casino has no unresolved complaints or regulatory issues.'
    },
    {
      question: 'Do you test withdrawal times yourself?',
      answer: 'Yes. We make real deposits and process real withdrawals at every casino we review. We track the time from withdrawal request to funds appearing in our account. This gives us accurate, first-hand data on payout speeds.'
    },
    {
      question: 'How do you rate casinos?',
      answer: 'Our rating system considers: game variety (20%), bonuses and promotions (15%), withdrawal speed (20%), customer support (15%), security and licensing (15%), mobile experience (10%), and overall player satisfaction (5%). Each category is scored independently, then weighted to create the final rating.'
    },
    {
      question: 'Can I trust your bonus information?',
      answer: 'Absolutely. We verify all bonus terms by reading the full terms and conditions, testing the bonus ourselves when possible, and confirming details with customer support. We also note any restrictions, wagering requirements, and expiration dates.'
    },
    {
      question: 'What if I have a problem with a casino you recommended?',
      answer: 'Contact us immediately. We take player complaints seriously and will investigate any issues. If we find that a casino has changed its practices or misled players, we\'ll update our review and may remove our recommendation. We also provide resources for filing formal complaints with the UK Gambling Commission.'
    },
    {
      question: 'Do you review all UK casinos?',
      answer: 'We focus on casinos that accept UK players and hold UK Gambling Commission licenses. We prioritize casinos with active marketing, positive player feedback, or unique features. If you\'d like us to review a specific casino, contact us and we\'ll consider adding it to our testing queue.'
    },
    {
      question: 'How do you handle responsible gambling?',
      answer: 'We promote responsible gambling on every page. We provide links to GamCare, GambleAware, and other support resources. We never encourage excessive gambling, and we highlight tools like deposit limits, self-exclusion, and reality checks. If you need help, please visit BeGambleAware.org.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      {/* JSON-LD SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })
        }}
      />

      <div className="min-h-screen bg-titanium-950 pt-20 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Frequently Asked Questions</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Everything you need to know about how we review casinos and how to use our platform.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="glass rounded-xl border-2 border-titanium-800 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-titanium-900/50 transition-colors"
                >
                  <span className="text-white font-semibold text-lg pr-4">{faq.question}</span>
                  {openIndex === index ? (
                    <FaChevronUp className="text-neon-blue flex-shrink-0" />
                  ) : (
                    <FaChevronDown className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Additional Help */}
          <div className="max-w-3xl mx-auto mt-12 glass rounded-xl p-8 border-2 border-neon-blue/30 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Still Have Questions?</h2>
            <p className="text-gray-300 mb-6">
              Can't find what you're looking for? We're here to help.
            </p>
            <a
              href="mailto:contact@spintitanium.com"
              className="inline-block bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;


